import { LinearProgressProps, Box, LinearProgress, useTheme } from '@mui/material'
import React from 'react'
import { Step } from './progressSteps'

type Props = LinearProgressProps & {
    maxStep: number
    step: number
    step1?: number
    step2?: number
    step3?: number
    step4?: number
}

type SProps = {
    value: number
    step: number
    active?: boolean
    relativeSpace?: string
}

const BORDER_SIZE = 1;
const BarProgress = ({
    value,
    step,
    active,
    relativeSpace = '25%',
    color,
    isFirst = false,
    isLast = false,
}: SProps & { color?: string; isFirst?: boolean; isLast?: boolean }) => {
    const muiTheme = useTheme()
    return (
        <Box sx={{ width: relativeSpace}}>
            <Box sx={{ position: 'relative' }}>
                <LinearProgress
                    variant="determinate"
                    className="h-6 steped-sm"
                    style={{
                        backgroundColor: muiTheme.palette.background.default,
                        
                    }}
                    value={value}
                    sx={{
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: value > 0 ? color : muiTheme.palette.background.default,
                        },
                        border: BORDER_SIZE,
                        borderColor: color,
                        borderStyle: 'solid',
                        borderRadius: 0,
                        borderLeft: isFirst ? `${BORDER_SIZE}px solid ${color}` : 0,
                        borderRight: isLast ? `${BORDER_SIZE}px solid ${color}` : 0,
                        borderTop: `${BORDER_SIZE}px solid ${color}`,
                        borderBottom: `${BORDER_SIZE}px solid ${color}`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        zIndex: 1,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Step stepNumber={step} active={!!active} />
                </Box>
            </Box>
            <Box sx={{ minWidth: 35 }}></Box>
        </Box>
    )
}

export const BarsProgress = (p: Props) => {
    const muiTheme = useTheme()
    const {
        step1 = 100,
        step2 = 50,
        step3 = 0,
        step4 = 0,
        maxStep,
        step,
    } = p

    const relativeSpace = `${100 / maxStep}%`

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
            }}
        >
            <BarProgress
                value={step1}
                step={1}
                active={step === 1}
                relativeSpace={relativeSpace}
                isFirst={true}
                isLast={maxStep === 1}
            />
            <BarProgress
                value={step2}
                step={2}
                active={step === 2}
                relativeSpace={relativeSpace}
                isFirst={false}
                isLast={maxStep === 2}
            />
            <BarProgress
                value={step3}
                step={3}
                active={step === 3}
                relativeSpace={relativeSpace}
                isFirst={false}
                isLast={maxStep === 3}
            />
            {maxStep === 4 && (
                <BarProgress
                    value={step4}
                    step={4}
                    active={step === 4}
                    relativeSpace={relativeSpace}
                    isFirst={false}
                    isLast={true}
                />
            )}
        </Box>
    )
}

type SimpleBarProgressProps = {
    value: number;
};

export const SimpleBarProgress = (p: SimpleBarProgressProps) => {
    const muiTheme = useTheme()
    const { value } = p

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem',
            }}
        >
            <Box sx={{ width: '100%' }}>
                <Box sx={{ position: 'relative' }}>
                    <LinearProgress
                        variant="determinate"
                        className="steped-sm"
                        style={{
                            backgroundColor: muiTheme.palette.background.default,
                            height: '8px',
                        }}
                        value={value}
                        sx={{
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: value > 0 ? muiTheme.palette.primary.main : muiTheme.palette.background.default,
                            },
                            border: BORDER_SIZE,
                            borderColor: muiTheme.palette.primary.dark,
                            borderStyle: 'solid',
                            borderRadius: 0,
                            borderLeft: `${BORDER_SIZE}px solid ${muiTheme.palette.primary.dark}`,
                            borderRight: `${BORDER_SIZE}px solid ${muiTheme.palette.primary.dark}`,
                            borderTop: `${BORDER_SIZE}px solid ${muiTheme.palette.primary.dark}`,
                            borderBottom: `${BORDER_SIZE}px solid ${muiTheme.palette.primary.dark}`,
                        }}
                    />
                </Box>
            </Box>
        </Box>
    )
}
